import React, { useEffect, useMemo, useState } from "react";
import {
  Search,
  RotateCw,
  SlidersHorizontal,
  Plus,
  ChevronDown,
  Eraser,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useDebounce } from "use-debounce";
import { hasPermission } from "../../../utils/permissionUtils";
import MobileAdvancedFilterSheet from "../../../components/ui/MobileAdvancedFilterSheet";
import {
  DOCUMENT_TYPE_OPTIONS,
  SIGNER_ROLE_OPTIONS,
  STATUS_TABS,
  URGENCY_OPTIONS,
} from "./signingConstants";

const EMPTY_FILTERS = {
  keyword: "",
  status: "",
  document_type: "",
  signer_role: "",
  urgency: "",
  issued_from: "",
  issued_to: "",
};

const STATUS_OPTIONS = STATUS_TABS.filter((t) => t.key).map((t) => ({
  value: t.key,
  label: t.label,
}));

const ToSelectOptions = (options) => options.map((o) => ({ value: o.value, label: o.label }));

const SigningFilter = ({ onNewClick, onFilterChange, onRefresh, value }) => {
  const [advanced, setAdvanced] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS });
  const [debouncedKeyword] = useDebounce(filters.keyword, 400);

  const isFiltering = useMemo(
    () => Object.values(filters).some((v) => String(v || "").trim() !== ""),
    [filters]
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Đồng bộ filter từ parent (mini dashboard) — không đụng keyword đang gõ
  useEffect(() => {
    if (!value) return;
    setFilters((prev) => {
      const nextStatus = value.status || "";
      const nextDoc = value.document_type || "";
      const nextSigner = value.signer_role || "";
      const nextUrgency = value.urgency || "";
      const nextFrom = value.issued_from || "";
      const nextTo = value.issued_to || "";
      if (
        prev.status === nextStatus &&
        prev.document_type === nextDoc &&
        prev.signer_role === nextSigner &&
        prev.urgency === nextUrgency &&
        prev.issued_from === nextFrom &&
        prev.issued_to === nextTo
      ) {
        return prev;
      }
      return {
        ...prev,
        status: nextStatus,
        document_type: nextDoc,
        signer_role: nextSigner,
        urgency: nextUrgency,
        issued_from: nextFrom,
        issued_to: nextTo,
      };
    });
  }, [
    value?.status,
    value?.document_type,
    value?.signer_role,
    value?.urgency,
    value?.issued_from,
    value?.issued_to,
  ]);

  // Chỉ debounce keyword — các filter khác gọi onFilterChange ngay trong updateFilter
  useEffect(() => {
    onFilterChange?.({ ...filters, keyword: debouncedKeyword });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedKeyword]);

  const updateFilter = (name, value) => {
    const updated = { ...filters, [name]: value };
    setFilters(updated);
    if (name !== "keyword") {
      onFilterChange?.(updated);
    }
  };

  const clearFilters = () => {
    const cleared = { ...EMPTY_FILTERS };
    setFilters(cleared);
    onFilterChange?.(cleared);
  };

  const ActionButton = ({ icon: Icon, onClick, title, active }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`h-11 w-11 flex items-center justify-center rounded-xl border transition-all duration-200 flex-shrink-0 ${
        active
          ? "bg-red-50 border-red-200 text-red-600 shadow-sm"
          : "bg-white border-gray-200 text-gray-500 hover:border-red-400 hover:text-red-500"
      }`}
    >
      <Icon size={18} />
    </button>
  );

  const SelectBox = ({ label, value, options, onChange, className = "w-full" }) => (
    <div className={`flex flex-col gap-1.5 ${className}`}>
      <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1">
        {label}
      </label>
      <div className="relative group">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="appearance-none w-full bg-white border border-gray-200 text-gray-700 py-3 px-4 pr-10 rounded-2xl text-sm font-medium shadow-sm focus:outline-none focus:border-red-500 focus:ring-4 focus:ring-red-500/5 transition-all cursor-pointer"
        >
          <option value="">Tất cả {label}</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <ChevronDown
          size={16}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none group-hover:text-red-500 transition-colors"
        />
      </div>
    </div>
  );

  const fieldClass = "w-full sm:w-[15.5rem]";
  const dateFieldClass = "w-full sm:w-[12rem]";

  const AdvancedFields = ({ fieldWidth = "w-full" }) => (
    <>
      <SelectBox
        label="Trạng thái"
        value={filters.status}
        options={STATUS_OPTIONS}
        onChange={(v) => updateFilter("status", v)}
        className={fieldWidth}
      />
      <SelectBox
        label="Loại VB"
        value={filters.document_type}
        options={ToSelectOptions(DOCUMENT_TYPE_OPTIONS)}
        onChange={(v) => updateFilter("document_type", v)}
        className={fieldWidth}
      />
      <SelectBox
        label="Người ký"
        value={filters.signer_role}
        options={ToSelectOptions(SIGNER_ROLE_OPTIONS)}
        onChange={(v) => updateFilter("signer_role", v)}
        className={fieldWidth}
      />
      <SelectBox
        label="Mức độ khẩn"
        value={filters.urgency}
        options={ToSelectOptions(URGENCY_OPTIONS)}
        onChange={(v) => updateFilter("urgency", v)}
        className={fieldWidth}
      />
    </>
  );

  if (isMobile) {
    return (
      <div className="mb-6 space-y-4" data-tour="hrm-signing-filter">
        <div className="flex items-stretch gap-2">
          <div className="relative flex-1 group">
            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors"
              size={18}
            />
            <input
              type="text"
              placeholder="Tìm mã / tiêu đề / số hiệu..."
              value={filters.keyword}
              onChange={(e) => updateFilter("keyword", e.target.value)}
              className="h-11 w-full bg-white border border-gray-200 pl-11 pr-4 rounded-2xl focus:outline-none focus:border-red-500 shadow-sm transition-all font-medium text-gray-700"
            />
          </div>
          <ActionButton
            icon={SlidersHorizontal}
            onClick={() => setAdvanced(!advanced)}
            active={advanced || isFiltering}
            title="Lọc nâng cao"
          />
          <ActionButton icon={RotateCw} onClick={() => onRefresh?.()} title="Làm mới" />
        </div>

        <MobileAdvancedFilterSheet
          open={advanced}
          onClose={() => setAdvanced(false)}
          title="Bộ lọc nâng cao"
          footer={
            <>
              <button
                type="button"
                onClick={clearFilters}
                className="flex-1 py-4 border border-gray-200 text-gray-700 rounded-2xl font-bold"
              >
                XÓA LỌC
              </button>
              <button
                type="button"
                onClick={() => setAdvanced(false)}
                className="flex-1 py-4 bg-red-600 text-white rounded-2xl font-bold shadow-lg shadow-red-200"
              >
                ÁP DỤNG
              </button>
            </>
          }
        >
          <div className="grid grid-cols-1 gap-4">
            <AdvancedFields />
            <div className="flex gap-3">
              <div className="flex flex-col gap-1.5 w-full">
                <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1">
                  Từ ngày
                </label>
                <input
                  type="date"
                  value={filters.issued_from}
                  onChange={(e) => updateFilter("issued_from", e.target.value)}
                  className="w-full bg-white border border-gray-200 text-gray-700 py-2.5 px-4 rounded-2xl text-sm font-medium shadow-sm focus:outline-none focus:border-red-500 focus:ring-4 focus:ring-red-500/5 transition-all"
                />
              </div>
              <div className="flex flex-col gap-1.5 w-full">
                <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1">
                  Đến ngày
                </label>
                <input
                  type="date"
                  value={filters.issued_to}
                  onChange={(e) => updateFilter("issued_to", e.target.value)}
                  className="w-full bg-white border border-gray-200 text-gray-700 py-2.5 px-4 rounded-2xl text-sm font-medium shadow-sm focus:outline-none focus:border-red-500 focus:ring-4 focus:ring-red-500/5 transition-all"
                />
              </div>
            </div>
          </div>
        </MobileAdvancedFilterSheet>

        {hasPermission("hrm.signing", "C") && (
          <button
            type="button"
            onClick={onNewClick}
            className="fixed bottom-20 right-5 bg-gradient-to-r from-red-700 to-red-800 text-white w-14 h-14 rounded-full shadow-lg flex items-center justify-center hover:scale-105 active:scale-95 z-40"
          >
            <Plus size={20} />
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm mb-6"
      data-tour="hrm-signing-filter"
    >
      <div className="flex flex-nowrap items-center justify-between gap-4">
        <div className="flex flex-nowrap items-center gap-3">
          <div className="relative w-full max-w-sm group flex-shrink-0">
            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors"
              size={18}
            />
            <input
              type="text"
              placeholder="Tìm mã / tiêu đề / số hiệu..."
              value={filters.keyword}
              onChange={(e) => updateFilter("keyword", e.target.value)}
              className="w-full bg-gray-50 border-transparent border-2 focus:bg-white focus:border-red-500 pl-11 pr-4 py-2.5 rounded-2xl outline-none transition-all font-medium text-gray-700"
            />
          </div>

          <ActionButton icon={RotateCw} onClick={() => onRefresh?.()} title="Làm mới" />
          <ActionButton
            icon={SlidersHorizontal}
            onClick={() => setAdvanced(!advanced)}
            active={advanced || isFiltering}
            title="Lọc nâng cao"
          />
          {isFiltering && (
            <button
              type="button"
              onClick={clearFilters}
              className="text-xs font-bold text-red-500 hover:text-red-700 flex items-center gap-1 uppercase tracking-wider transition-all ml-2 whitespace-nowrap flex-shrink-0"
            >
              <Eraser size={14} /> Xóa lọc
            </button>
          )}
        </div>

        {hasPermission("hrm.signing", "C") && (
          <button
            type="button"
            onClick={onNewClick}
            className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-red-700 to-red-800 text-white rounded-full font-bold hover:shadow-lg hover:shadow-red-100 transition-all active:scale-95 uppercase text-xs tracking-wider whitespace-nowrap flex-shrink-0"
          >
            <Plus size={18} /> Tạo yêu cầu
          </button>
        )}
      </div>

      <AnimatePresence>
        {advanced && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="flex flex-wrap items-end gap-4 mt-6 pt-6 border-t border-gray-100">
              <AdvancedFields fieldWidth={fieldClass} />

              <div className={`flex flex-col gap-1.5 ${dateFieldClass}`}>
                <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1">
                  Từ ngày ban hành
                </label>
                <input
                  type="date"
                  value={filters.issued_from}
                  onChange={(e) => updateFilter("issued_from", e.target.value)}
                  className="w-full bg-white border border-gray-200 text-gray-700 py-2.5 px-4 rounded-2xl text-sm font-medium shadow-sm focus:outline-none focus:border-red-500 focus:ring-4 focus:ring-red-500/5 transition-all"
                />
              </div>
              <div className={`flex flex-col gap-1.5 ${dateFieldClass}`}>
                <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1">
                  Đến ngày ban hành
                </label>
                <input
                  type="date"
                  value={filters.issued_to}
                  onChange={(e) => updateFilter("issued_to", e.target.value)}
                  className="w-full bg-white border border-gray-200 text-gray-700 py-2.5 px-4 rounded-2xl text-sm font-medium shadow-sm focus:outline-none focus:border-red-500 focus:ring-4 focus:ring-red-500/5 transition-all"
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SigningFilter;
